import { useState, useRef, useEffect, memo, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { getOptimizedImageUrl, getInitialsFallbackUrl } from '@/lib/imageOptimization';

interface OptimizedImageProps {
  src: string | null;
  alt: string;
  className?: string;
  fallback?: React.ReactNode;
  fallbackName?: string; // For generating initials fallback
  width?: number;
  height?: number;
  loading?: 'lazy' | 'eager';
  priority?: boolean;
  sizes?: string;
}

/**
 * OptimizedImage - Performance-optimized image component
 * 
 * Features:
 * - Native lazy loading for below-the-fold images
 * - Intersection Observer for progressive loading
 * - Fallback support with auto-generated initials
 * - Proper width/height to prevent CLS
 * - Browser caching optimization
 * - Smooth fade-in transitions
 */
function OptimizedImageComponent({
  src,
  alt,
  className,
  fallback,
  fallbackName,
  width = 44,
  height = 44,
  loading = 'lazy',
  priority = false,
  sizes,
}: OptimizedImageProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isInView, setIsInView] = useState(priority);
  const imgRef = useRef<HTMLImageElement>(null);

  // Get optimized URL
  const optimizedSrc = getOptimizedImageUrl(src, { width, height });

  useEffect(() => {
    if (priority || !imgRef.current) {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: '100px', // Preload slightly before visible
        threshold: 0.01,
      }
    );

    observer.observe(imgRef.current);

    return () => observer.disconnect();
  }, [priority]);

  // Reset state if src changes
  useEffect(() => {
    setHasError(false);
    setIsLoaded(false);
  }, [src]);

  const handleLoad = useCallback(() => setIsLoaded(true), []);
  const handleError = useCallback(() => setHasError(true), []);

  // Handle missing or errored images
  if (!optimizedSrc || hasError) {
    if (fallback) {
      return <>{fallback}</>;
    }
    // Auto-generate initials fallback
    if (fallbackName) {
      return (
        <img
          src={getInitialsFallbackUrl(fallbackName, width)}
          alt={alt}
          width={width}
          height={height}
          className={className}
          aria-hidden="true"
        />
      );
    }
    return null;
  }

  return (
    <img
      ref={imgRef}
      src={isInView ? optimizedSrc : undefined}
      data-src={optimizedSrc}
      alt={alt}
      width={width}
      height={height}
      sizes={sizes}
      loading={priority ? 'eager' : loading}
      decoding={priority ? 'sync' : 'async'}
      fetchPriority={priority ? 'high' : 'auto'}
      onLoad={handleLoad}
      onError={handleError}
      className={cn(
        'transition-opacity duration-200',
        isLoaded ? 'opacity-100' : 'opacity-0',
        className
      )}
      style={{
        contentVisibility: priority ? 'visible' : 'auto',
      }}
    />
  );
}

// Memoize to prevent unnecessary re-renders
export const OptimizedImage = memo(OptimizedImageComponent, (prev, next) => {
  return (
    prev.src === next.src &&
    prev.width === next.width &&
    prev.height === next.height &&
    prev.priority === next.priority &&
    prev.className === next.className
  );
});
